// The live half of a gesture (SPEC-viewfinder §4.1), and it is exactly three
// things: distances on the rows already on the list, membership, and which
// marks have entered the ring.
//
// One function for the rail's two, and it returns distances and membership
// ONLY — never a new order, never a new clock. „Nieposortowane traci dla mnie
// sens, ale z drugiej strony live też bym chciał mieć" (owner, after the
// prototype): the ruling is live membership, settled order.
//
// Pure, like everything in `derive/`: no DOM, no clock, no singleton. It takes
// the previous derivation and the gesture's live centre and radius, which is
// why `derive` was built to take `centre` and `radius` from its input rather
// than reaching for a store (#244 S7a).
import { haversine } from '../shared/geo.ts';
import type { Item } from '../shared/model.ts';
import type { LiveMeasure } from '../state/gesture.ts';
import type { Derived } from './derive.ts';

/** Placeless rows sort after every positioned one, exactly as in `order.ts`. */
const FAR = Number.POSITIVE_INFINITY;

/**
 * What one frame of a gesture measures, for the rail and the two counts.
 *
 * The universe is `prev`'s own three sets — `placed`, `beyond` and `placeless`,
 * whose union is `onTheRail`. A gesture moves the centre and the radius; it
 * never changes WHICH items are on the rail, because that is the snapshot's
 * business and a poll mid-gesture rebuilds nothing (§4.1). So this re-divides
 * one known set rather than re-deriving it, and `buildAxis` is not run again:
 * running it would re-order the list under the finger, which is the one thing
 * the owner ruled against.
 *
 * A placeless row belongs to every centre, so it is inside at every radius
 * (§2.1) and its distance stays `null` — the `—` the column already spells.
 */
export function remeasure(prev: Derived, centre: { lat: number; lon: number }, radius: number): LiveMeasure {
  const metres = new Map<string, number | null>();
  const inside = new Set<string>();
  const placed: Item[] = [];
  const beyond: Item[] = [];

  for (const item of [...prev.placed, ...prev.beyond]) {
    const away = item.position ? haversine(centre, item.position) : null;
    metres.set(item.id, away);
    if (away != null && away <= radius) {
      inside.add(item.id);
      placed.push(item);
    } else beyond.push(item);
  }
  // Nearest first, which is what §2.6's „Najbliżej" reads off the head of it
  // and what keeps the dots' own DOM order meaningful.
  beyond.sort((a, b) => (metres.get(a.id) ?? FAR) - (metres.get(b.id) ?? FAR));

  for (const item of prev.placeless) {
    metres.set(item.id, null);
    inside.add(item.id);
  }
  return { metres, inside, placed, beyond };
}

/**
 * Which marks have entered the ring under the finger, and which have left it
 * (§4.1, point 3 — „entering marks appear at once").
 *
 * It is measured over `prev.nearby` and NOT over {@link LiveMeasure}, and the
 * two sets are genuinely different: `nearby` is what the circle may draw — it
 * ignores `view.lines`, so it holds every vehicle — while the rail's sets hold
 * only what would have a row. Asking the rail's membership about a mark would
 * have taken every other line's vehicle off the disc on the first frame.
 *
 * `nearby` reaches 1,6 × the settled radius, which is the measured way the
 * prototype did it (#238, round 2): a gesture cannot widen the ring, so an item
 * beyond that is one the settle will rebuild for anyway.
 *
 * `drawn` is the ids the circle has a mark for right now; what comes back is
 * the delta, so the view creates a handful of elements per frame rather than
 * rebuilding a layer under the finger.
 */
export function markDelta(
  prev: Derived,
  centre: { lat: number; lon: number },
  radius: number,
  drawn: ReadonlyMap<string, unknown>,
): { enter: Item[]; leave: string[] } {
  const holds = new Set<string>();
  const enter: Item[] = [];
  for (const item of prev.nearby) {
    if (!item.position || haversine(centre, item.position) > radius) continue;
    holds.add(item.id);
    if (!drawn.has(item.id)) enter.push(item);
  }
  // Walked, not spread. `drawn` holds every mark on the disc — at a 5 km radius
  // that is every vehicle in the city — and copying it into an array on every
  // measurement is allocation the 150 ms burst cannot afford (§4.3). The map
  // the view already keeps is read directly, and only the leavers are built.
  const leave: string[] = [];
  for (const id of drawn.keys()) if (!holds.has(id)) leave.push(id);
  return { enter, leave };
}
